import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CalendarX2, CheckCircle2, Clock4, AlertTriangle, XCircle, Plus, Trash2, Loader2, CreditCard,
} from "lucide-react";
import { currentCycleDates, formatDayLabel } from "@/lib/schedule";
import { formatDMY } from "@/lib/dateFormat";
import { todayISO, crTime, toISODate } from "@/lib/dateFormat";

const today = () => todayISO();

function StatusBadge({ status }) {
  const map = {
    auto_justified: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: <CheckCircle2 className="h-3 w-3" />, label: "Auto-justificada" },
    approved: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: <CheckCircle2 className="h-3 w-3" />, label: "Aprobada" },
    pending_review: { cls: "bg-amber-100 text-amber-800 border-amber-200", icon: <Clock4 className="h-3 w-3" />, label: "Pendiente" },
    needs_review: { cls: "bg-amber-100 text-amber-800 border-amber-200", icon: <Clock4 className="h-3 w-3" />, label: "Pendiente" },
    mixed: { cls: "bg-blue-100 text-blue-800 border-blue-200", icon: <AlertTriangle className="h-3 w-3" />, label: "Mixto" },
    rejected: { cls: "bg-red-100 text-red-800 border-red-200", icon: <XCircle className="h-3 w-3" />, label: "Rechazada" },
    withdrawn: { cls: "bg-slate-100 text-slate-700 border-slate-200", icon: <Trash2 className="h-3 w-3" />, label: "Retirada" },
  };
  const m = map[status] || map.pending_review;
  return (
    <Badge variant="outline" className={`${m.cls} border gap-1 inline-flex`}>
      {m.icon}{m.label}
    </Badge>
  );
}

export default function MyAbsences() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [groups, setGroups] = useState({});
  const [requests, setRequests] = useState([]);
  const [quotas, setQuotas] = useState({}); // enrollment_id → snapshot
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);

  // El historial guarda TODO (es el expediente), pero se separa el ciclo en
  // curso de los cerrados: así el contador de arriba cuadra con lo que se ve.
  // `cycle_start` lo da el backend (día siguiente a la última clase del ciclo
  // ya facturado); sin ciclos cerrados, todo es del ciclo actual.
  const esDelCicloActual = (r) => {
    const desde = quotas[r.enrollment_id]?.cycle_start;
    if (!desde) return true;
    return (r.dates || []).some((d) => String(d.date || "") >= desde);
  };
  const { delCiclo, anteriores } = useMemo(() => {
    const delCiclo = [], anteriores = [];
    for (const r of requests) (esDelCicloActual(r) ? delCiclo : anteriores).push(r);
    return { delCiclo, anteriores };
  }, [requests, quotas]);   // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try {
      const [e, g, r] = await Promise.all([
        api.get("/enrollments", { params: { student_id: user.user_id } }),
        api.get("/groups"),
        api.get("/absence-requests"),
      ]);
      const gmap = Object.fromEntries((g.data || []).map((x) => [x.id, x]));
      setEnrollments(e.data || []);
      setGroups(gmap);
      setRequests(r.data || []);
      const snap = {};
      for (const enr of e.data || []) {
        try {
          const q = await api.get("/absence-requests/quota", {
            params: { enrollment_id: enr.id },
          });
          snap[enr.id] = q.data;
        } catch { /* ignore */ }
      }
      setQuotas(snap);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.user_id) load(); }, [user?.user_id]);

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t("loading")}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-absences-page">
      <PageHeader
        title={t("my_absences_title")}
        subtitle={t("my_absences_subtitle")}
        testId="my-absences-header"
        action={
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button data-testid="new-absence-btn" className="btn-brand">
                <Plus className="h-4 w-4 mr-1" /> {t("new_absence_request")}
              </Button>
            </DialogTrigger>
            <NewAbsenceDialog
              enrollments={enrollments}
              groups={groups}
              quotas={quotas}
              onClose={() => setOpenCreate(false)}
              onCreated={() => { setOpenCreate(false); load(); }}
            />
          </Dialog>
        }
      />

      {/* Quota cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {enrollments.length === 0 && (
          <Card className="p-6 text-center text-slate-500 col-span-full">
            {t("no_active_enrollments")}
          </Card>
        )}
        {enrollments.map((enr) => {
          const g = groups[enr.group_id];
          const q = quotas[enr.id];
          return (
            <Card key={enr.id} className="p-5 space-y-2" data-testid={`quota-card-${enr.id}`}>
              <div className="text-xs text-slate-500 uppercase tracking-wider">{g?.name || "—"}</div>
              <div className="font-display text-3xl font-bold text-slate-900" data-testid={`quota-used-${enr.id}`}>
                {!q ? "—" : q.unlimited ? "∞" : `${q.used}/${q.cycle_quota}`}
              </div>
              <div className="text-sm text-slate-600">
                {q?.unlimited
                  ? t("unlimited_justified_student_hint")
                  : q && q.cycle_quota > 0
                  ? t("remaining_cancellations_cycle", { left: q.remaining, total: q.cycle_quota })
                  : t("no_cycle_active")}
              </div>
              <div className="text-[11px] text-slate-500">
                {q?.unlimited ? "" : t("policy_hours_label", { hours: q?.policy_hours || 24 })}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Historial: guarda todo, separado por ciclo */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700 flex items-center gap-2">
            <CalendarX2 className="h-4 w-4" /> {t("my_absence_history")}
          </h3>
        </div>
        {requests.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="no-absence-requests">
            {t("no_absence_requests")}
          </div>
        ) : (
          <>
            {delCiclo.length > 0 && (
              <div data-testid="absence-history-current">
                <SectionLabel>{t("absence_history_current_cycle")}</SectionLabel>
                <div className="divide-y divide-slate-100">
                  {delCiclo.map((r) => <RequestRow key={r.id} r={r} group={groups[r.group_id]} t={t} />)}
                </div>
              </div>
            )}
            {anteriores.length > 0 && (
              <div data-testid="absence-history-previous">
                <SectionLabel>{t("absence_history_past_cycles")}</SectionLabel>
                <div className="divide-y divide-slate-100 opacity-70">
                  {anteriores.map((r) => <RequestRow key={r.id} r={r} group={groups[r.group_id]} t={t} />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const SectionLabel = ({ children }) => (
  <div className="px-5 py-2 bg-slate-50 border-y border-slate-200 text-[11px] font-semibold
                  uppercase tracking-wider text-slate-500">
    {children}
  </div>
);

function RequestRow({ r, group, t }) {
  return (
    <div className="p-4 hover:bg-slate-50" data-testid={`absence-request-${r.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900">{group?.name || "—"}</span>
          <StatusBadge status={r.status_summary} />
          <span className="text-[11px] text-slate-500" data-testid={`absence-submitted-${r.id}`}>
            {/* Momento exacto del envío, en hora de Costa Rica. */}
            Enviada el {formatDMY(toISODate(new Date(r.submitted_at)))} a las {crTime(r.submitted_at)}
          </span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(r.dates || []).map((d, i) => (
            <Badge key={i} variant="outline" className="text-[11px] gap-1 inline-flex">
              {formatDMY(d.date)}
              <span className="text-slate-400">·</span>
              <StatusBadge status={d.status} />
            </Badge>
          ))}
        </div>
        {r.reason && <p className="mt-2 text-sm text-slate-600 whitespace-pre-wrap">{r.reason}</p>}
        {r.admin_note && (
          <div className="mt-2 bg-blue-50 border-l-2 border-blue-300 px-3 py-1.5 text-sm text-blue-900">
            <strong className="text-xs uppercase tracking-wider">{t("admin_note")}:</strong> {r.admin_note}
          </div>
        )}
      </div>
    </div>
  );
}

function NewAbsenceDialog({ enrollments, groups, quotas, onClose, onCreated }) {
  const { t, lang } = useI18n();
  // Hide enrollments that don't have an active cycle (cycle_quota == 0) — submitting them
  // would 404 since they have no group/program/payment linked yet.
  const usable = enrollments.filter((e) => {
    const q = quotas[e.id];
    return q && q.cycle_quota > 0;
  });
  const [enrollmentId, setEnrollmentId] = useState(usable[0]?.id || "");
  const [dates, setDates] = useState([""]);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  // Extra-sessions + attendance drive the "available dates" dropdown so
  // students only see legitimate class dates (no more picking a random day).
  const [extraSessions, setExtraSessions] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [otherRequests, setOtherRequests] = useState([]);

  const enrollment = usable.find((e) => e.id === enrollmentId);
  const group = enrollment ? groups[enrollment.group_id] : null;

  useEffect(() => {
    if (!enrollment?.group_id) return;
    Promise.all([
      api.get("/extra-sessions", { params: { group_id: enrollment.group_id } }).catch(() => ({ data: [] })),
      api.get("/attendance", { params: { group_id: enrollment.group_id, student_id: enrollment.student_id } }).catch(() => ({ data: [] })),
      api.get("/absence-requests").catch(() => ({ data: [] })),
    ]).then(([ex, at, ar]) => {
      setExtraSessions(ex.data || []);
      setAttendance(at.data || []);
      setOtherRequests(ar.data || []);
    });
  }, [enrollment?.group_id, enrollment?.student_id]);

  // Dates the student can cancel = the classes of their CURRENT cycle that
  // haven't happened yet and aren't already handled. Uses the shared
  // `currentCycleDates` helper so this list can NEVER drift from the progress
  // card the student sees in "Mi progreso" / "Mi ciclo actual".
  const availableDates = useMemo(() => {
    if (!group || !enrollment) return [];
    const quotaSnap = quotas[enrollmentId] || {};
    const totalPerCycle = enrollment.classes_included || quotaSnap.classes_included || 0;
    const cc = currentCycleDates({
      group, enrollment, extraSessions, attendance, totalPerCycle,
    });
    if (!cc) return [];
    const todayIso = todayISO();
    // Statuses that mean "this date is already handled" — hide from dropdown
    const skipStatus = new Set(["present", "absent", "justified", "unjustified", "unjustified_consumed", "teacher_cancelled", "not_counted", "removed"]);
    // Dates already in a pending/approved request (to avoid duplicates)
    const pendingDates = new Set();
    for (const req of (otherRequests || [])) {
      if (req.student_id && enrollment?.student_id && req.student_id !== enrollment.student_id) continue;
      for (const d of (req.dates || [])) {
        if (d.status === "pending_review" || d.status === "auto_justified" || d.status === "approved") {
          pendingDates.add(d.date);
        }
      }
    }
    return cc.dates
      .filter((d) => d.date >= todayIso && !skipStatus.has(d.status) && !pendingDates.has(d.date))
      .map((d) => d.date);
  }, [group, enrollment, extraSessions, attendance, otherRequests, quotas, enrollmentId]);

  const quota = quotas[enrollmentId];

  const addDate = () => setDates((d) => [...d, ""]);
  const removeDate = (i) => setDates((d) => d.filter((_, idx) => idx !== i));
  const setDateAt = (i, v) => setDates((d) => d.map((x, idx) => (idx === i ? v : x)));

  const submit = async () => {
    if (!enrollmentId) {
      toast.error(t("error_generic"));
      return;
    }
    const cleaned = Array.from(new Set(dates.filter((x) => x).map((x) => x.trim())));
    if (cleaned.length === 0) {
      toast.error(t("at_least_one_date"));
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/absence-requests", {
        enrollment_id: enrollmentId,
        dates: cleaned,
        reason,
      });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setSaving(false);
    }
  };

  if (result) {
    return (
      <DialogContent className="max-w-lg" data-testid="absence-result-dialog">
        <DialogHeader>
          <DialogTitle>{t("absence_request_received")}</DialogTitle>
          <DialogDescription>{t("absence_request_received_subtitle")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {(result.dates || []).map((d, i) => (
            <div
              key={i}
              className={`p-3 rounded-lg border ${
                d.status === "auto_justified"
                  ? "bg-emerald-50 border-emerald-200"
                  : "bg-amber-50 border-amber-200"
              }`}
              data-testid={`result-date-${d.date}`}
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="font-semibold">{d.date}</span>
                <StatusBadge status={d.status} />
              </div>
              <p className="text-xs text-slate-700 mt-1">
                {d.status === "auto_justified"
                  ? t("auto_justified_msg")
                  : d.exceeds_quota
                  ? t("over_quota_msg")
                  : t("late_notice_msg", { hours: d.policy_hours })}
              </p>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={onCreated} className="btn-brand" data-testid="absence-result-close">
            {t("close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  }

  return (
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-absence-dialog">
      <DialogHeader>
        <DialogTitle>{t("new_absence_request")}</DialogTitle>
        <DialogDescription>{t("new_absence_dialog_subtitle")}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        {usable.length === 0 && (
          <div
            className="bg-amber-50 border border-amber-300 rounded-lg p-4 text-sm text-amber-900 flex items-start gap-3"
            data-testid="no-usable-enrollment"
          >
            <CreditCard className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-900 mb-1">{t("payment_required_title")}</p>
              <p className="text-amber-800 leading-relaxed">{t("no_usable_enrollment")}</p>
            </div>
          </div>
        )}
        <div>
          <Label className="text-xs">{t("enrollment")}</Label>
          <Select value={enrollmentId} onValueChange={setEnrollmentId} disabled={usable.length === 0}>
            <SelectTrigger data-testid="enrollment-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {usable.map((enr) => {
                const g = groups[enr.group_id];
                return (
                  <SelectItem key={enr.id} value={enr.id}>
                    {g?.name || enr.id}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {quota && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-700">{t("cycle_quota_label")}</span>
              <span className="font-semibold" data-testid="quota-modal-remaining">
                {quota.unlimited ? "∞" : t("quota_left_short", { left: quota.remaining, total: quota.cycle_quota })}
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              {quota.unlimited
                ? t("unlimited_justified_student_hint")
                : t("policy_hours_label", { hours: quota.policy_hours })}
            </p>
            {!quota.unlimited && quota.remaining <= 0 && quota.cycle_quota > 0 && (
              <p className="text-[11px] text-amber-700 mt-1">⚠️ {t("quota_exhausted_warning")}</p>
            )}
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs">{t("dates_to_cancel")}</Label>
            <Button size="sm" variant="outline" onClick={addDate} data-testid="add-date-btn" disabled={availableDates.length === 0 || dates.length >= availableDates.length}>
              <Plus className="h-3.5 w-3.5 mr-1" /> {t("add_date")}
            </Button>
          </div>
          <div className="space-y-2">
            {availableDates.length === 0 ? (
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2" data-testid="no-available-dates">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                {t("no_available_dates_to_cancel")}
              </div>
            ) : dates.map((d, i) => (
              <div key={i} className="flex items-center gap-2">
                <Select value={d} onValueChange={(v) => setDateAt(i, v)}>
                  <SelectTrigger data-testid={`absence-date-${i}`} className="flex-1">
                    <SelectValue placeholder={t("choose_class_date")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {availableDates
                      // Hide dates already picked in the OTHER rows to avoid dupes
                      .filter((av) => av === d || !dates.includes(av))
                      .map((av) => (
                        <SelectItem key={av} value={av} data-testid={`available-date-${av}`}>
                          {formatDayLabel(av, lang)} · {formatDMY(av, lang)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {dates.length > 1 && (
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => removeDate(i)}
                    data-testid={`remove-date-${i}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs">{t("reason")}</Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={t("absence_reason_placeholder")}
            data-testid="absence-reason"
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
        <Button
          onClick={submit}
          disabled={saving || !enrollmentId || usable.length === 0}
          className="btn-brand"
          data-testid="submit-absence-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          {t("submit_request")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
